import { blobToBytes } from '../../core/blob-bytes';
import { EngineFailure } from '../../core/errors';
import { defaultLoadPdfjs, type PdfJsLike } from '../document/pdfjs-loader';

/**
 * 把 PDF 页渲染成图片（AI 识别通道的输入端）。
 *
 * 为什么在浏览器渲染而不是服务端：渲染需要 canvas，浏览器现成；服务端要额外背一套
 * WASM 渲染链与内存配额。渲染结果压成 JPEG（扫描件对 JPEG 友好，体积远小于 PNG），
 * 每页渲染完立即释放 canvas，避免多页时内存峰值叠加。
 */

/** 渲染目标宽度（像素）：够模型看清正文，又不至于让单页 base64 过大。 */
export const AI_RENDER_TARGET_WIDTH = 1400;
export const AI_RENDER_JPEG_QUALITY = 0.8;
/** 单张图片（非 PDF）缩放后的最大宽度。 */
export const AI_IMAGE_MAX_WIDTH = 1600;

export interface RenderPagesDeps {
  loadPdfjs?: () => Promise<PdfJsLike>;
}

export interface RenderPagesOptions {
  maxPages: number;
  signal?: AbortSignal;
  onProgress?: (info: { page: number; total: number }) => void;
}

function abortError(): Error {
  const error = new Error('任务已取消');
  error.name = 'AbortError';
  return error;
}

function createCanvas(width: number, height: number): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  return canvas;
}

/** 逐页渲染为 JPEG data URL；页数超过上限时只渲染前 maxPages 页（调用方需自行拒绝超限）。 */
export async function renderPdfPages(
  file: File,
  options: RenderPagesOptions,
  deps: RenderPagesDeps = {},
): Promise<string[]> {
  const load = deps.loadPdfjs ?? defaultLoadPdfjs;
  const pdfjs = await load();
  const bytes = await blobToBytes(file);

  let document: Awaited<ReturnType<PdfJsLike['getDocument']>['promise']> | null = null;
  try {
    document = await pdfjs.getDocument({ data: bytes }).promise;
    const total = Math.min(document.numPages, options.maxPages);
    if (total <= 0) throw new EngineFailure('MALFORMED_FILE', 'PDF 里没有任何页面');

    const pages: string[] = [];
    for (let index = 1; index <= total; index += 1) {
      if (options.signal?.aborted) throw abortError();

      const page = await document.getPage(index);
      const base = page.getViewport({ scale: 1 });
      const scale = Math.min(3, Math.max(0.5, AI_RENDER_TARGET_WIDTH / Math.max(1, base.width)));
      const viewport = page.getViewport({ scale });

      const canvas = createCanvas(Math.ceil(viewport.width), Math.ceil(viewport.height));
      const context = canvas.getContext('2d');
      if (!context) throw new EngineFailure('ENGINE_UNAVAILABLE', '浏览器不支持 Canvas，无法渲染扫描页');

      await page.render({ canvas, canvasContext: context, viewport }).promise;
      pages.push(canvas.toDataURL('image/jpeg', AI_RENDER_JPEG_QUALITY));

      // 立即释放：多页文档下把峰值压到单页大小
      canvas.width = 0;
      canvas.height = 0;
      options.onProgress?.({ page: index, total });
    }
    return pages;
  } finally {
    await Promise.resolve(document?.destroy?.()).catch(() => undefined);
  }
}

/**
 * 图片类源文件 → data URL。
 *
 * 优先用 canvas 缩放到合理宽度并转 JPEG（省流量、省上游 token）；
 * 环境不支持 `createImageBitmap` 时退回直接读取原图（由服务端上限兜底）。
 */
export async function imageToDataUrl(file: File): Promise<string> {
  if (typeof createImageBitmap !== 'function') return readAsDataUrl(file);

  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, AI_IMAGE_MAX_WIDTH / Math.max(1, bitmap.width));
    const canvas = createCanvas(Math.max(1, Math.round(bitmap.width * scale)), Math.max(1, Math.round(bitmap.height * scale)));
    const context = canvas.getContext('2d');
    if (!context) return readAsDataUrl(file);
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL('image/jpeg', AI_RENDER_JPEG_QUALITY);
    bitmap.close?.();
    return dataUrl;
  } catch {
    return readAsDataUrl(file);
  }
}

function readAsDataUrl(file: File): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error ?? new Error('图片读取失败'));
    reader.readAsDataURL(file);
  });
}
